import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserRole, selectCurrentUser } from '../../store/slices/authSlice';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { adminApi, clientApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusTimeline from '../../components/common/StatusTimeline';
import PhotoThumb from '../../components/common/PhotoThumb';
import NoteThread from '../../components/common/NoteThread';
import AuditLogPanel from '../../components/common/AuditLogPanel';
import { statusLabel } from '../../utils/statusLabels';
import { downloadBomComponentsExcel } from '../../utils/bomExcelExport';
import { format } from 'date-fns';

/** Returns true for 15- or 18-character Salesforce IDs — never display these. */
function isSalesforceId(value) {
  return typeof value === 'string' && /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(value);
}


export default function AssignmentBomView() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const [modal, setModal] = useState(null); // { approve: bool }
  const [cvaModal, setCvaModal] = useState(false); // CLIENT_VENDOR_ADMIN approval modal
  const [cvaNote, setCvaNote]   = useState('');
  const [cvaRejectModal, setCvaRejectModal] = useState(false); // CVA reject (send back to vendor)
  const [cvaRejectNote, setCvaRejectNote]   = useState('');
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const isClientVendorAdmin = userRole === 'CLIENT_VENDOR_ADMIN';
  const isSuperAdmin = userRole === 'PLATFORM_SUPER_ADMIN';
  const [notes, setNotes] = useState('');

  // ── Per-row inline-edit state ────────────────────────────────────────────
  // Only one BOM row can be edited at a time.
  const [editingRowId,    setEditingRowId]    = useState(null); // id of the row in edit mode
  const [draftRow,        setDraftRow]        = useState(null); // mutable copy of that row
  // ── Delete confirmation state ────────────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // id of row pending deletion

  const { data, isLoading, isError } = useQuery(
    ['admin-assignment', id],
    () => adminApi.getAssignment(id),
    { refetchOnWindowFocus: false }
  );

  // axios interceptor unwraps response → data = ApiResponse, data.data = RequestAssignmentResponse
  const assignment = data?.data;
  const bomArticles = assignment?.partArticles?.filter(p => !p.mainProduct) || [];
  const canDownloadSubmittedBom = Boolean(assignment?.submittedAt && bomArticles.length > 0);

  const handleDownloadBom = () => {
    try {
      downloadBomComponentsExcel({
        components: bomArticles,
        finishedGoodCode: assignment?.bobFinishedGoodCode || assignment?.fgName,
      });
      toast.success('BOM downloaded successfully.');
    } catch {
      toast.error('Could not download the BOM Excel file. Please try again.');
    }
  };

  const reviewMutation = useMutation(adminApi.companyReview, {
    onSuccess: (_, vars) => {
      toast.success(vars.approved ? 'Submission approved!' : 'Submission rejected.');
      qc.invalidateQueries(['admin-assignment', id]);
      qc.invalidateQueries('admin-review-queue');
      qc.invalidateQueries('admin-stats');
      setModal(null);
      setNotes('');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Review failed'),
  });

  const clientVendorApprovalMutation = useMutation(
    ({ notes }) => clientApi.finalApproval(Number(id), notes),
    {
      onSuccess: () => {
        toast.success('✅ Fully Approved!');
        qc.invalidateQueries(['admin-assignment', id]);
        setCvaModal(false);
        setCvaNote('');
      },
      onError: (err) => toast.error(err?.response?.data?.error || 'Approval failed'),
    }
  );

  const cvaRejectMutation = useMutation(
    ({ notes }) => clientApi.finalReject(Number(id), notes),
    {
      onSuccess: () => {
        toast.success('Rejected — sent back to the vendor for revision.');
        qc.invalidateQueries(['admin-assignment', id]);
        qc.invalidateQueries(['admin-audit-log']);
        setCvaRejectModal(false);
        setCvaRejectNote('');
      },
      onError: (err) => toast.error(err?.response?.data?.error || 'Rejection failed'),
    }
  );

  const updateBomMutation = useMutation(
    (payload) => adminApi.updateBom(Number(id), payload),
    {
      onSuccess: (data) => {
        toast.success('BOM row saved.');
        qc.setQueryData(['admin-assignment', id], data);
        setEditingRowId(null);
        setDraftRow(null);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Save failed'),
    }
  );

  const cancelMutation = useMutation(
    (reason) => adminApi.cancelAssignment(Number(id), reason),
    {
      onSuccess: () => {
        toast.success('Assignment permanently cancelled.');
        qc.invalidateQueries(['admin-assignment', id]);
        qc.invalidateQueries('finished-goods');
        qc.invalidateQueries('admin-stats');
        setCancelModal(false);
        setCancelReason('');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Cancel failed'),
    }
  );

  const deleteBomMutation = useMutation(
    (componentId) => adminApi.deleteBomComponent(Number(id), componentId),
    {
      onSuccess: (data) => {
        toast.success('BOM component deleted.');
        qc.setQueryData(['admin-assignment', id], data);
        setDeleteConfirmId(null);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed'),
    }
  );

  /** Enter per-row edit mode — copies the server row into local draft state. */
  const handleEditRow = (comp) => {
    setEditingRowId(comp.id);
    setDraftRow({
      id:                     comp.id,
      partName:               comp.partName             ?? '',
      partDescription:        comp.articleName          ?? '',
      stockType:              normalizeStockType(comp.stockType),
      partColor:              comp.partColor             ?? '',
      width:                  comp.width                 ?? '',
      length:                 comp.length                ?? '',
      height:                 comp.height                ?? '',
      measurementUnit:        comp.measurementUnit       ?? 'IN',
      partWeight:             comp.partWeight            ?? '',
      weightUnit:             normalizeWeightUnit(comp.weightUnit),
      category:               normalizeCategory(comp.category),
      vendorPartNumber:       String(comp.vendorPartNumber ?? '').toUpperCase(),
      vendorFactoryName:      comp.vendorFactoryName     ?? '',
      supplierName:           comp.supplierName          ?? '',
      supplierPartNumber:     String(comp.supplierPartNumber ?? '').toUpperCase(),
      uom:                    comp.uom                   ?? '',
      totalQtyInFg:           comp.totalQtyInFg          ?? '',
      costPerUom:             comp.unitPrice             ?? '',
      currency:               comp.currency              ?? 'USD',
    });
  };

  /** Discard edits — restore original server values for that row. */
  const handleDiscardRow = () => {
    setEditingRowId(null);
    setDraftRow(null);
  };

  /** Save the single edited row to the backend. */
  const handleSaveRow = () => {
    const r = draftRow;
    updateBomMutation.mutate({
      assignmentId: Number(id),
      components: [{
        id:                     r.id,
        partName:               r.partName,
        partDescription:        r.partDescription,
        stockType:              r.stockType,
        partColor:              r.partColor,
        width:                  r.width,
        length:                 r.length,
        height:                 r.height,
        measurementUnit:        r.measurementUnit,
        partWeight:             r.partWeight,
        weightUnit:             r.weightUnit,
        category:               r.category,
        vendorPartNumber:       r.vendorPartNumber,
        vendorFactoryName:      r.vendorFactoryName,
        supplierName:           r.supplierName,
        supplierPartNumber:     r.supplierPartNumber,
        uom:                    r.uom,
        totalQtyInFg:           r.totalQtyInFg !== '' ? Number(r.totalQtyInFg) : null,
        costPerUom:             r.costPerUom   !== '' ? Number(r.costPerUom)   : null,
        currency:               r.currency,
      }],
    });
  };

  const setDraftField = (field, value) =>
    setDraftRow(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <LoadingSpinner />;
  if (isError)   return <div className="text-center py-16 text-red-400">Failed to load assignment. Please try again.</div>;
  if (!assignment) return <div className="text-center py-16 text-gray-400">Assignment not found.</div>;

  const mainArticle = assignment.partArticles?.find(p => p.mainProduct);
  // Super Admin and Client-Vendor Admin both review vendor submissions.
  const canReview   = assignment.status === 'VENDOR_SUBMITTED' && (isSuperAdmin || isClientVendorAdmin);
  // BOM editable only before client review (PENDING_FINAL_APPROVAL and CLIENT_APPROVED are locked).
  const canEdit = ['VENDOR_SUBMITTED', 'COMPANY_APPROVED'].includes(assignment.status);
  // Super admin and CVA can cancel before PENDING_FINAL_APPROVAL, CLIENT_APPROVED (Salesforce synced), or already CANCELLED
  const canCancel = (isSuperAdmin || isClientVendorAdmin) && !['PENDING_FINAL_APPROVAL', 'CLIENT_APPROVED', 'CANCELLED'].includes(assignment.status);

  return (
    <div className="w-full pb-10 space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1">
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {assignment.finishedProduct || assignment.fgName || `Assignment #${id}`}
          </h1>
          <p className="text-sm text-gray-400 font-mono mt-0.5">
            {assignment.bobFinishedGoodCode || assignment.fgName} · Assignment #{id}
            {assignment.vendorName && <> · Vendor: <span className="text-gray-600 font-medium not-italic">{assignment.vendorName}</span></>}
            {assignment.vendorEmail && <span className="text-gray-400"> ({assignment.vendorEmail})</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {assignment.submittedAt && (
            <span className="text-xs text-gray-400">
              Submitted {format(new Date(assignment.submittedAt), 'dd MMM yyyy HH:mm')}
            </span>
          )}
          <StatusBadge status={assignment.status} />
          {canCancel && (
            <button
              onClick={() => setCancelModal(true)}
              className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 transition-colors">
              🚫 Cancel Process
            </button>
          )}
        </div>
      </div>

      {/* ── Lifecycle Timeline ──────────────────────────────────────────── */}
      <StatusTimeline
        fgCreatedAt={assignment.fgCreatedAt}
        assignedAt={assignment.assignedAt}
        submittedAt={assignment.submittedAt}
        reviewedAt={assignment.reviewedAt}
        fgClientApprovedAt={assignment.fgClientApprovedAt}
        status={assignment.status}
        companyNotes={assignment.companyNotes}
        vendorName={assignment.vendorName}
        vendorCompanyName={assignment.vendorCompanyName}
        hideHistory={true}
        assignmentId={Number(id)}
        fetchHistory={adminApi.getAssignmentHistory}
        companyLabel="OmniDrive"
        showRawPerformer={true}
      />

      {assignment.vendorNotes && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-semibold text-blue-700 mb-1">Vendor Notes</p>
          <p className="text-sm text-blue-800">{assignment.vendorNotes}</p>
        </div>
      )}

      {/* ── Key Dates ──────────────────────────────────────────────────── */}
      <LifecycleDates assignment={assignment} />

      {/* ── Finished Good Details (read-only) ──────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <span className="text-lg">📦</span>
          <h2 className="text-lg font-bold text-gray-900">Finished Good Details</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">main product</span>
        </div>

        {/* ── FG Catalog Fields ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          <ReadField label="Product Title"                  value={assignment.requestTitle || assignment.fgName} />
          <ReadField label="Finished Good Code"             value={assignment.bobFinishedGoodCode} />
          <ReadField label="Country of Origin"              value={assignment.countryOfOrigin} />
          <ReadField label="Top Category"                   value={assignment.topCategory} />
          <ReadField label="Category"                       value={assignment.category} />
          <ReadField label="Collection Name"                value={assignment.collectionName} />
          <ReadField label="Finished Product"               value={assignment.finishedProduct} />
          <ReadField label="Vendor Finished Good Model"     value={assignment.vendorFinishedGoodModel} />
          <ReadField label="Factory Code"                   value={assignment.factoryCode} />
          <ReadField label="Factory Name"                   value={assignment.factoryName} />
          <ReadField label="Finish"                         value={assignment.finish} />
          <ReadField label="Color"                          value={assignment.color} />
          <ReadField label="Vendor Product Code"            value={assignment.vendorProductCode} />
          <ReadField label="Package Sales Set"              value={assignment.packageSalesSet} />
          <ReadField label="Package Sales Set Name"         value={assignment.packageSalesSetName} />
          <ReadField label="Package Component Qty"          value={assignment.packageComponentQty} />
          <ReadField label="Vendor Code (Internal to Bobs)" value={assignment.vendorCode} highlight />
          <ReadField label="Vendor Name"                    value={assignment.fgVendorName} />
        </div>
        {assignment.productDescription && (
          <div className="mb-4">
            <ReadField label="Product Description" value={assignment.productDescription} />
          </div>
        )}

        {/* ── Vendor-Submitted Main Product Info ── */}
        {mainArticle && (
          <div className="border-t border-gray-100 pt-4 mt-2 mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Vendor-Submitted Main Product Info</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                ['Finished Good Article ID', isSalesforceId(mainArticle?.fgArticleId) ? null : mainArticle?.fgArticleId],
                ['Collection Name',          mainArticle?.collectionName || assignment.collectionName],
                ['Product Description',      mainArticle?.productDescription || assignment.productDescription],
                ['Article Group',            mainArticle?.articleGroup],
                ['Style / Generic',          mainArticle?.genericStyle],
              ].map(([label, value]) => (
                <ReadField key={label} label={label} value={value} />
              ))}
            </div>
          </div>
        )}

        {/* Main product photos */}
        {mainArticle?.files?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Main Product Photo</p>
            <div className="flex gap-3 flex-wrap">
              {mainArticle.files.map((f, i) => (
                <PhotoThumb key={i} fileId={f.id} label={f.originalName} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── BOM Table ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">⚙️</span>
          <h2 className="text-lg font-bold text-gray-900">Components (Bill of Materials)</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {bomArticles.length} component{bomArticles.length !== 1 ? 's' : ''}
          </span>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {canDownloadSubmittedBom && (
              <button
                onClick={handleDownloadBom}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                📥 Download BOM
              </button>
            )}
            {canEdit && editingRowId && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full font-medium">
                ✏️ Editing row — save or discard before editing another
              </span>
            )}
          </div>
        </div>
        {canEdit && (
          <p className={`text-xs mb-4 ${editingRowId ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
            * All fields are mandatory
          </p>
        )}


        {bomArticles.length === 0 ? (
          <p className="text-sm text-gray-400 py-10 text-center">No BOM components submitted.</p>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-sm border-collapse" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-200">
                  <Th w="40px">#</Th>
                  <Th required>Part Name</Th>
                  <Th required>Part Description</Th>
                  <Th required w="110px">Stock Type</Th>
                  <Th required>Part Color</Th>
                  <Th required w="90px">Measurement Unit</Th>
                  <Th required w="80px">L (Length)</Th>
                  <Th required w="80px">W (Width)</Th>
                  <Th required w="80px">H (Height)</Th>
                  <Th required w="140px">Weight Unit</Th>
                  <Th required>Weight</Th>
                  <Th required w="200px">Category</Th>
                  <Th required>Vendor Part Number</Th>
                  <Th required>Vendor's Factory Name</Th>
                  <Th required>Supplier Name</Th>
                  <Th required>Supplier Part Number</Th>
                  <Th required w="100px">UoM</Th>
                  <Th required w="110px">Total Qty in FG</Th>
                  <Th required w="140px">Cost per UoM (USD)</Th>
                  <Th w="140px">Photo</Th>
                  {canEdit && <Th w="170px">Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bomArticles.map((comp, idx) =>
                  editingRowId === comp.id ? (
                    <InlineEditRow
                      key={comp.id}
                      row={draftRow}
                      idx={idx}
                      onChange={setDraftField}
                      onSave={handleSaveRow}
                      onDiscard={handleDiscardRow}
                      isSaving={updateBomMutation.isLoading}
                    />
                  ) : (
                    <ReadRow
                      key={comp.id}
                      comp={comp}
                      idx={idx}
                      canEdit={canEdit}
                      isLocked={editingRowId !== null}
                      onEdit={() => handleEditRow(comp)}
                      deleteConfirmId={deleteConfirmId}
                      onDeleteRequest={() => setDeleteConfirmId(comp.id)}
                      onDeleteCancel={() => setDeleteConfirmId(null)}
                      onDeleteConfirm={() => deleteBomMutation.mutate(comp.id)}
                      isDeleting={deleteBomMutation.isLoading}
                    />
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Notes thread ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Notes
          <span className="text-xs text-gray-400 font-normal">— visible to OmniDrive, vendor &amp; account</span>
        </h3>
        <NoteThread
          assignmentId={Number(id)}
          fetchNotes={adminApi.getAssignmentNotes}
          postNote={adminApi.addAssignmentNote}
          updateNote={adminApi.updateAssignmentNote}
          deleteNote={adminApi.deleteAssignmentNote}
          canPost={assignment.status !== 'CANCELLED'}
          canModerate={isSuperAdmin || isClientVendorAdmin}
          currentUserId={currentUser?.userId}
          queryKey="admin-notes"
        />
      </div>

      {/* ── Audit Log ───────────────────────────────────────────────────── */}
      <AuditLogPanel
        assignmentId={Number(id)}
        fetchHistory={adminApi.getAssignmentHistory}
        queryKey="admin-audit-log"
      />

      {/* ── CLIENT_VENDOR_ADMIN Approval ─────────────────────────────────── */}
      {isClientVendorAdmin && assignment.status === 'PENDING_FINAL_APPROVAL' && (
        <div className="bg-white rounded-xl border-2 border-blue-300 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Final Approval — CVA Sign-Off</h2>
              <p className="text-sm text-gray-500">OmniDrive and the Account team have approved. Give your final sign-off to trigger Salesforce sync.</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setCvaRejectModal(true)}
              className="px-6 py-2.5 bg-white hover:bg-red-50 text-red-600 text-sm font-bold rounded-lg border border-red-300 transition-colors">
              ❌ Reject &amp; Send Back to Vendor
            </button>
            <button
              onClick={() => setCvaModal(true)}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg transition-colors shadow-sm">
              ✅ Give Final Approval &amp; Sync to Salesforce
            </button>
          </div>
        </div>
      )}

      {/* ── CLIENT_VENDOR_ADMIN Reject Modal ────────────────────────────── */}
      {cvaRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">❌ Reject &amp; Send Back to Vendor</h3>
            <p className="text-sm text-gray-500 mb-4">The request will be reassigned to the vendor to revise the finished good and BOM details. Please explain what needs to change.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea value={cvaRejectNote} onChange={e => setCvaRejectNote(e.target.value)} rows={3}
              placeholder="What should the vendor fix?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setCvaRejectModal(false)} disabled={cvaRejectMutation.isLoading}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={() => cvaRejectMutation.mutate({ notes: cvaRejectNote.trim() })}
                disabled={cvaRejectMutation.isLoading || !cvaRejectNote.trim()}
                className="px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50">
                {cvaRejectMutation.isLoading ? 'Processing…' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CLIENT_VENDOR_ADMIN Approval Modal ──────────────────────────── */}
      {cvaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">✅ Final Approval — Sync to Salesforce</h3>
            <p className="text-sm text-gray-500 mb-4">This is the final step. Confirming will mark this as fully approved and trigger the Salesforce writeback.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea value={cvaNote} onChange={e => setCvaNote(e.target.value)} rows={3}
              placeholder="Add review notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setCvaModal(false)} disabled={clientVendorApprovalMutation.isLoading}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={() => clientVendorApprovalMutation.mutate({ notes: cvaNote })}
                disabled={clientVendorApprovalMutation.isLoading}
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                {clientVendorApprovalMutation.isLoading ? 'Processing…' : 'Confirm Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review actions ──────────────────────────────────────────────── */}
      {canReview ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-800">OmniDrive Review</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {editingRowId !== null
                ? 'Save or discard your BOM row edit before approving or rejecting.'
                : 'Approve to forward to the client, or reject to ask the vendor to revise.'}
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={() => { setModal({ approve: false }); setNotes(''); }}
              disabled={editingRowId !== null}
              title={editingRowId !== null ? 'Save your BOM edits first' : undefined}
              className="px-5 py-2 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed">
              ✕ Reject
            </button>
            <button
              onClick={() => { setModal({ approve: true }); setNotes(''); }}
              disabled={editingRowId !== null}
              title={editingRowId !== null ? 'Save your BOM edits first' : undefined}
              className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed">
              ✓ Approve
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center text-sm text-gray-500">
          {assignment.status === 'COMPANY_APPROVED' && '✅ OmniDrive has approved. Awaiting Account review.'}
          {assignment.status === 'PENDING_FINAL_APPROVAL' && '⏳ Account has approved. Awaiting CVA final sign-off.'}
          {assignment.status === 'COMPANY_REJECTED' && '❌ OmniDrive has rejected this submission. The vendor has been notified to revise.'}
          {assignment.status === 'CLIENT_APPROVED'  && '✅ CVA has given final approval. Salesforce sync triggered. Fully complete.'}
          {assignment.status === 'CLIENT_REJECTED'  && '❌ The client has rejected this submission.'}
          {assignment.status === 'CVA_REJECTED'     && '❌ CVA has rejected this submission. It has been reassigned to the vendor for revision.'}
          {assignment.status === 'CANCELLED'         && '🚫 This process has been permanently cancelled by OmniDrive Super Admin.'}
          {!['COMPANY_APPROVED','COMPANY_REJECTED','PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED','CANCELLED'].includes(assignment.status) &&
            <>Status: <strong>{statusLabel(assignment.status)}</strong></>}
          {assignment.companyNotes && <span className="ml-2 text-gray-400">— Notes: {assignment.companyNotes}</span>}
        </div>
      )}

      {/* ── Cancel Process Modal (PLATFORM_SUPER_ADMIN only) ───────────── */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Cancel This Process?</h3>
                <p className="text-sm text-red-600 font-medium">⚠️ This action cannot be undone.</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-800">
              Cancelling will permanently stop this finished good process. The assignment will be
              marked as <strong>Cancelled</strong> and cannot be recovered or restarted. All
              parties (vendor, client) will lose access to the workflow.
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional but recommended)</label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Briefly explain why this process is being cancelled…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => { setCancelModal(false); setCancelReason(''); }}
                disabled={cancelMutation.isLoading}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Go Back
              </button>
              <button
                onClick={() => cancelMutation.mutate(cancelReason)}
                disabled={cancelMutation.isLoading}
                className="px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50">
                {cancelMutation.isLoading ? 'Cancelling…' : 'Yes, Cancel Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve / Reject modal ─────────────────────────────────────── */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">
              {modal.approve ? '✅ OmniDrive Approval' : '❌ OmniDrive Rejection'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {modal.approve
                ? 'OmniDrive is approving this submission. It will be forwarded to the client for final review.'
                : 'OmniDrive is rejecting this submission. The vendor will be notified to revise and resubmit.'}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Add notes for the vendor or client..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setModal(null)} disabled={reviewMutation.isLoading}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={() => reviewMutation.mutate({ assignmentId: Number(id), approved: modal.approve, notes })}
                disabled={reviewMutation.isLoading}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 font-medium ${modal.approve ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {reviewMutation.isLoading ? 'Processing...' : (modal.approve ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Read-only BOM row ─────────────────────────────────────────────────────────
// canEdit  — OmniDrive has edit rights for this assignment's current status
// isLocked — another row is already in edit mode
// onEdit   — callback to enter edit mode for this row

function ReadRow({ comp, idx, canEdit, isLocked, onEdit,
                   deleteConfirmId, onDeleteRequest, onDeleteCancel, onDeleteConfirm, isDeleting }) {
  const lockedTitle    = 'Save or discard the row you are currently editing first';
  const isPendingDel   = deleteConfirmId === comp.id;
  const otherPendingDel = deleteConfirmId !== null && deleteConfirmId !== comp.id;

  return (
    <tr className={`align-top hover:bg-gray-50/60 ${isPendingDel ? 'bg-red-50/40' : ''}`}>
      <td className="px-3 py-3 text-gray-400 text-xs font-medium">{idx + 1}</td>
      <td className="px-3 py-3 font-medium text-gray-800 whitespace-nowrap">{comp.partName || '—'}</td>
      <td className="px-3 py-3 text-gray-700">{comp.articleName || '—'}</td>
      <td className="px-3 py-3">
        {comp.stockType
          ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium
              ${normalizeStockType(comp.stockType) === 'SPECIAL' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
              {normalizeStockType(comp.stockType)}
            </span>
          : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-3 text-gray-500">{comp.partColor || '—'}</td>
      <td className="px-3 py-3 text-gray-500 uppercase">{comp.measurementUnit || 'IN'}</td>
      <td className="px-3 py-3 text-gray-500">{comp.length || '—'}</td>
      <td className="px-3 py-3 text-gray-500">{comp.width || '—'}</td>
      <td className="px-3 py-3 text-gray-500">{comp.height || '—'}</td>
      <td className="px-3 py-3 text-gray-500 text-sm uppercase">
        {comp.weightUnit ? `${['lb','oz','kg','g'].includes(comp.weightUnit) ? {'lb':'Pounds (lb)','oz':'Ounces (oz)','kg':'Kilograms (kg)','g':'Grams (g)'}[comp.weightUnit] : comp.weightUnit}` : '—'}
      </td>
      <td className="px-3 py-3 text-gray-500">{comp.partWeight || '—'}</td>
      <td className="px-3 py-3 text-gray-500 text-sm uppercase">{comp.category || '—'}</td>
      <td className="px-3 py-3 text-gray-500 text-sm uppercase">{comp.vendorPartNumber || '—'}</td>
      <td className="px-3 py-3 text-gray-500">{comp.vendorFactoryName || '—'}</td>
      <td className="px-3 py-3 text-gray-500">{comp.supplierName || '—'}</td>
      <td className="px-3 py-3 text-gray-500 text-sm uppercase">{comp.supplierPartNumber || '—'}</td>
      <td className="px-3 py-3 text-gray-700">{comp.uom || '—'}</td>
      <td className="px-3 py-3 text-gray-700 text-right">{comp.totalQtyInFg ?? '—'}</td>
      <td className="px-3 py-3 text-right">
        {comp.unitPrice != null
          ? <span className="font-mono text-gray-700">${Number(comp.unitPrice).toFixed(2)} <span className="text-gray-400 text-xs">{comp.currency || 'USD'}</span></span>
          : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-col gap-1.5 items-start">
          {comp.files?.map((f, i) => <PhotoThumb key={i} fileId={f.id} label={f.originalName} small />)}
          {(!comp.files || comp.files.length === 0) && <span className="text-gray-300 text-xs">—</span>}
        </div>
      </td>
      {canEdit && (
        <td className="px-3 py-3">
          {isPendingDel ? (
            /* ── Inline delete confirmation ── */
            <div className="flex flex-col gap-1">
              <span className="text-xs text-red-600 font-medium mb-0.5">Delete?</span>
              <div className="flex gap-1">
                <button
                  onClick={onDeleteConfirm}
                  disabled={isDeleting}
                  className="px-2 py-1 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-medium">
                  {isDeleting ? '…' : 'Yes'}
                </button>
                <button
                  onClick={onDeleteCancel}
                  disabled={isDeleting}
                  className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                  No
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <button
                onClick={isLocked ? undefined : onEdit}
                disabled={isLocked || otherPendingDel}
                title={isLocked ? lockedTitle : 'Edit this BOM row'}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors
                  ${isLocked || otherPendingDel
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'}`}>
                ✏️ Edit
              </button>
              <button
                onClick={isLocked || otherPendingDel ? undefined : onDeleteRequest}
                disabled={isLocked || otherPendingDel}
                title={isLocked ? lockedTitle : 'Delete this BOM row'}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors
                  ${isLocked || otherPendingDel
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-400'}`}>
                🗑 Delete
              </button>
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

// ── Inline editable BOM row ───────────────────────────────────────────────────

const CELL_INPUT  = 'w-full px-2 py-1 border border-blue-200 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-800';
const CELL_SELECT = 'w-full px-2 py-1 border border-blue-200 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-800';
const ERR_INPUT   = 'w-full px-2 py-1 border border-red-400 rounded text-sm bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-400 text-gray-800';
const ERR_SELECT  = 'w-full px-2 py-1 border border-red-400 rounded text-sm bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-400 text-gray-800';
const isNumericDimension = (value) => /^\d+(\.\d+)?$/.test(String(value ?? '').trim());

function isRowValid(row) {
  const s = (v) => String(v ?? '').trim();
  return !!(
    s(row.partName) &&
    s(row.partDescription) &&
    s(row.partColor) &&
    [row.length, row.width, row.height].some(isNumericDimension) &&
    [row.length, row.width, row.height].every(value => !s(value) || isNumericDimension(value)) &&
    ['IN', 'CM'].includes(row.measurementUnit) &&
    s(row.partWeight) &&
    row.weightUnit &&
    row.category &&
    s(row.vendorPartNumber) &&
    s(row.vendorFactoryName) &&
    s(row.supplierName) &&
    s(row.supplierPartNumber) &&
    row.uom &&
    s(String(row.totalQtyInFg)) &&
    s(String(row.costPerUom))
  );
}

const UOM_OPTIONS = ['EACH', 'SET', 'PCS', 'PAIR', 'BOX', 'ROLL', 'MTR', 'KG', 'LTR'];

const CATEGORY_OPTIONS = [
  { group: 'Fabric & Cores', items: ['Casing', 'Full Cushion', 'Core', 'Toss Pillow', 'Back Pillow', 'Leather'] },
  { group: 'Wood', items: ['Drawers', 'Panels', 'Full Wood Chair', 'Wood Legs', 'Console & Lid'] },
  { group: 'Electronics', items: [
    'Motors/Actuator', 'Massage Motor', 'Switches', 'Hand Wand', 'Control Pad',
    'Wireless Charger', 'USB Charger', 'Transformer', 'Cup Holder', 'Control Box',
    'Remote Control', 'Kit - Control Box/Remote', 'Compressor', 'Airbag', 'Y-Cable',
    'Extension Cable', 'Power Cable', 'Other Cable', 'Power Outlet', 'Fuse', 'Socket',
    'Reading Light', 'Speakers', 'LED Light', 'Sensor Buttons', 'Battery', 'Other Electronics',
  ]},
  { group: 'Mechanisms', items: [
    'Mechanism Scissors', 'Sleeper - Queen', 'Sleeper - Full', 'Sleeper - Twin',
    'Mechanism Tubes', 'Mechanism Springs', 'Headrest Mechanism', 'Lumbar Mechanism',
    'Headrest/Lumbar Mechanism Kit', 'Mechanism Hardware', 'Mechanism Base', 'Other Mechanisms',
  ]},
  { group: 'Hardware', items: [
    'Pull Handle', 'Storage Hinge/Mech', 'Bracket', 'Side Glides', 'Underneath Glide',
    'Headrest Latch', 'Seat Springs', 'Soft Close Piston', 'Caster Wheel',
    'Drawer/Door Handle', 'Drawer/Door Knob', 'Rubber Coaster', 'Footrest Plastic/Metal Support',
    'Power Motion Connector', 'Stationary Connector', 'Tray Cover - Plastic',
    'Tray Cover with Wireless Charger', 'Electronic Hardware', 'Heating Pad',
    'Pole / Rod / Stand', 'Metal Sofa Legs', 'Plastic Sofa Legs', 'Other Hardware',
  ]},
];

const UPPERCASE_BOM_TEXT_FIELDS = new Set(['vendorPartNumber', 'supplierPartNumber']);
const normalizeStockType = (value) => String(value ?? '').trim().toUpperCase() || 'STOCK';
const normalizeWeightUnit = (value) => {
  const unit = String(value ?? '').trim().toLowerCase();
  if (unit === 'lb' || unit.includes('pound')) return 'lb';
  if (unit === 'oz' || unit.includes('ounce')) return 'oz';
  if (unit === 'kg' || unit.includes('kilogram')) return 'kg';
  if (unit === 'g' || unit.includes('gram')) return 'g';
  return unit;
};
const CATEGORY_VALUES = CATEGORY_OPTIONS.flatMap(({ group, items }) =>
  items.map(item => ({ value: `${group} > ${item}`, item }))
);
const normalizeCategory = (value) => {
  const category = String(value ?? '').trim();
  if (!category) return '';
  const comparable = category.toUpperCase();
  return CATEGORY_VALUES.find(({ value: option, item }) =>
    option.toUpperCase() === comparable || item.toUpperCase() === comparable
  )?.value || category;
};

function InlineEditRow({ row, idx, onChange, onSave, onDiscard, isSaving }) {
  const f = (field) => (e) => onChange(
    field,
    UPPERCASE_BOM_TEXT_FIELDS.has(field) ? e.target.value.toUpperCase() : e.target.value
  );
  const s = (v) => String(v ?? '').trim();
  const isValid = isRowValid(row);

  // Returns error or normal input class based on whether the field is empty
  const ic = (field) => s(row[field]) ? CELL_INPUT : ERR_INPUT;
  const sc = (field) => row[field] ? CELL_SELECT : ERR_SELECT;

  // Dimensions: error if ALL three are empty
  const dimErr = !s(row.width) && !s(row.length) && !s(row.height);
  const dc = (field) => (dimErr && !s(row[field])) ? ERR_INPUT : CELL_INPUT;

  return (
    <tr className="align-top bg-blue-50/40 ring-1 ring-inset ring-blue-200">
      <td className="px-3 py-2 text-blue-500 text-xs font-medium">{idx + 1}</td>
      <td className="px-2 py-2 min-w-[120px]"><input className={ic('partName')} value={row.partName} onChange={f('partName')} /></td>
      <td className="px-2 py-2 min-w-[150px]"><input className={ic('partDescription')} value={row.partDescription} onChange={f('partDescription')} /></td>
      <td className="px-2 py-2">
        {/* stockType always has a value — no error state needed */}
        <select className={CELL_SELECT} value={row.stockType} onChange={f('stockType')}>
          <option value="STOCK">STOCK</option>
          <option value="SPECIAL">SPECIAL</option>
        </select>
      </td>
      <td className="px-2 py-2 min-w-[100px]"><input className={ic('partColor')} value={row.partColor} onChange={f('partColor')} /></td>
      <td className="px-2 py-2 min-w-[90px]"><select className={sc('measurementUnit')} value={row.measurementUnit} onChange={f('measurementUnit')}><option value="IN">IN</option><option value="CM">CM</option></select></td>
      <td className="px-2 py-2 min-w-[80px]"><input type="number" min="0" step="any" className={dc('length')} value={row.length} onChange={f('length')} placeholder="L" /></td>
      <td className="px-2 py-2 min-w-[80px]"><input type="number" min="0" step="any" className={dc('width')}  value={row.width}  onChange={f('width')}  placeholder="W" /></td>
      <td className="px-2 py-2 min-w-[80px]"><input type="number" min="0" step="any" className={dc('height')} value={row.height} onChange={f('height')} placeholder="H" /></td>
      <td className="px-2 py-2 min-w-[110px]">
        <select className={sc('weightUnit')} value={row.weightUnit} onChange={f('weightUnit')}>
          <option value="">UNIT…</option>
          <option value="lb">POUNDS (LB)</option>
          <option value="oz">OUNCES (OZ)</option>
          <option value="kg">KILOGRAMS (KG)</option>
          <option value="g">GRAMS (G)</option>
        </select>
      </td>
      <td className="px-2 py-2 min-w-[90px]"><input className={ic('partWeight')} value={row.partWeight} onChange={f('partWeight')} /></td>
      <td className="px-2 py-2 min-w-[180px]">
        <select className={sc('category')} value={row.category} onChange={f('category')}>
          <option value="">SELECT CATEGORY…</option>
          {CATEGORY_OPTIONS.map(({ group, items }) => (
            <optgroup key={group} label={group.toUpperCase()}>
              {items.map(item => (
                <option key={`${group}-${item}`} value={`${group} > ${item}`}>{item.toUpperCase()}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </td>
      <td className="px-2 py-2 min-w-[120px]"><input className={`${ic('vendorPartNumber')} uppercase`} value={row.vendorPartNumber} onChange={f('vendorPartNumber')} /></td>
      <td className="px-2 py-2 min-w-[130px]"><input className={ic('vendorFactoryName')} value={row.vendorFactoryName} onChange={f('vendorFactoryName')} /></td>
      <td className="px-2 py-2 min-w-[120px]"><input className={ic('supplierName')} value={row.supplierName} onChange={f('supplierName')} /></td>
      <td className="px-2 py-2 min-w-[120px]"><input className={`${ic('supplierPartNumber')} uppercase`} value={row.supplierPartNumber} onChange={f('supplierPartNumber')} /></td>
      <td className="px-2 py-2 min-w-[80px]">
        <select className={sc('uom')} value={row.uom} onChange={f('uom')}>
          <option value="">Select…</option>
          {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 min-w-[80px]">
        <input className={s(String(row.totalQtyInFg)) ? CELL_INPUT : ERR_INPUT} type="number" value={row.totalQtyInFg} onChange={f('totalQtyInFg')} />
      </td>
      <td className="px-2 py-2 min-w-[110px]">
        <input className={s(String(row.costPerUom)) ? CELL_INPUT : ERR_INPUT} type="number" step="0.01" value={row.costPerUom} onChange={f('costPerUom')} />
      </td>
      <td className="px-3 py-2 text-xs text-gray-400 italic">Photo unchanged</td>
      {/* Actions — Save & Discard */}
      <td className="px-2 py-2">
        <div className="flex flex-col gap-1.5">
          <button
            onClick={onSave}
            disabled={isSaving || !isValid}
            title={!isValid ? 'Fill all required fields before saving' : undefined}
            className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {isSaving ? 'Saving…' : '💾 Save'}
          </button>
          <button
            onClick={onDiscard}
            disabled={isSaving}
            className="px-2.5 py-1 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            ✕ Discard
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Shared sub-components ────────────────────────────────────────────────────

function Th({ children, w, required }) {
  return (
    <th style={w ? { width: w, minWidth: w } : { minWidth: '120px' }}
      className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </th>
  );
}

function LifecycleDates({ assignment }) {
  const fmt = (d) => d ? format(new Date(d), 'dd MMM yyyy, hh:mm a') : '—';
  const reviewLabel = () => {
    if (!assignment.reviewedAt) return '—';
    if (['COMPANY_APPROVED', 'CLIENT_APPROVED'].includes(assignment.status)) return `Approved · ${fmt(assignment.reviewedAt)}`;
    if (['COMPANY_REJECTED', 'CLIENT_REJECTED', 'CVA_REJECTED'].includes(assignment.status))  return `Rejected · ${fmt(assignment.reviewedAt)}`;
    return fmt(assignment.reviewedAt);
  };

  const dates = [
    { icon: '🔄', label: 'FG Synced',            value: fmt(assignment.fgCreatedAt) },
    { icon: '📋', label: 'Assigned to Vendor',    value: fmt(assignment.assignedAt) },
    { icon: '📤', label: 'Vendor Submitted',       value: fmt(assignment.submittedAt) },
    { icon: '✅', label: 'OmniDrive Reviewed',     value: reviewLabel() },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Key Dates
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {dates.map(({ icon, label, value }) => (
          <div key={label} className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{icon} {label}</span>
            <span className="text-sm text-gray-800 font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadField({ label, value, highlight }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm min-h-[36px] break-words">
        {value != null && value !== ''
          ? <span className={highlight ? 'text-orange-600 font-medium' : 'text-gray-700'}>{value}</span>
          : <span className="text-gray-400">—</span>}
      </div>
    </div>
  );
}

// PhotoThumb is imported from ../../components/common/PhotoThumb
